import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
// import { directLogout, setLoggedIn } from '../features/auth/authSlice'; // Using RTK Query instead
import { enqueueSnackbar } from 'notistack';
import i18next from 'i18next';
import { authApi as authApiUrl } from '../utils/urls';
import { navigate as navigateInternally } from './navigation';

const baseQuery = fetchBaseQuery({
  baseUrl: '/',
  prepareHeaders: (headers, { endpoint }) => {
    const token = localStorage.getItem('token');

    if (token) {
      headers.set('token', token);
    }

    // Don't set Content-Type for file uploads - let the browser set it with boundary
    if (!endpoint?.includes('Upload')) {
      headers.set('Content-Type', 'application/json');
    }
    headers.set('Accept', 'application/json');
    return headers;
  },
});

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    const { status } = result.error;
    const suppress = args && typeof args === 'object' && args.suppressGenericErrorToast === true;

    const clearLocalSession = async () => {
      // Clear localStorage - the auth hooks will detect this and return logged out
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('roles');
      } catch {}
      // Also update RTK Query cache for components that are already mounted
      try {
        const { authApi: authApiSlice } = await import('../features/auth/authApi');
        api.dispatch(
          authApiSlice.util.updateQueryData('status', undefined, (draft) => {
            draft.user = null;
            draft.sessionId = null;
            draft.roles = null;
            draft.loggedIn = false;
          })
        );
      } catch {}
    };

    if (status === 405) {
      // Token not found on server: stop sending bad token and sync status to logged out
      await clearLocalSession();
    } else if (status === 401) {
      // Differentiate between expired session vs insufficient permissions
      try {
        const statusRes = await baseQuery(
          {
            url: authApiUrl(),
            method: 'POST',
            body: { method: 'status' },
          },
          api,
          extraOptions
        );
        const raw = statusRes && 'data' in statusRes ? statusRes.data : null;
        const statusData = raw && typeof raw === 'object' && 'data' in raw ? raw.data : raw;
        const user = statusData?.user;
        if (!user) {
          // Session is truly expired
          await clearLocalSession();
        } else {
          // Still authenticated: permission issue; redirect to dashboard
          if (!suppress) {
            enqueueSnackbar(i18next.t('httpClient.forbidden', 'access not permited'), { variant: 'error' });
          }
          try {
            navigateInternally('/dashboard', { replace: true });
          } catch {}
        }
      } catch {
        await clearLocalSession();
      }
    } else if (status === 403) {
      if (!suppress) {
        enqueueSnackbar(i18next.t('httpClient.forbidden', 'access not permited'), { variant: 'error' });
      }
    } else if (status === 500) {
      if (!suppress) {
        enqueueSnackbar(i18next.t('httpClient.serverError', 'server error'), { variant: 'error' });
      }
    } else if (status < 200 || status >= 300) {
      if (!suppress) {
        enqueueSnackbar(i18next.t('httpClient.fetchError', { url: args.url }), { variant: 'error' });
      }
    }
  }

  return result;
};
